/**
 * Module dependencies.
 */

import { chartPalette } from 'app/components/organisms/charts/charts';

/**
 * Export `badgePalette` — the preset swatch set for badge colors (reuses the
 * app's finance-tuned chart palette).
 */

export const badgePalette = chartPalette;

/**
 * Export `randomBadgeColor` — pick a random palette color, avoiding `exclude`.
 */

export function randomBadgeColor(exclude?: string): string {
  const pool = badgePalette.filter(color => color.toLowerCase() !== (exclude ?? '').toLowerCase());
  const list = pool.length ? pool : badgePalette;

  return list[Math.floor(Math.random() * list.length)];
}

/**
 * Parse a `#rrggbb` / `#rgb` hex into rgb components.
 */

function toRgb(hex: string): { r: number; g: number; b: number } {
  const clean = (hex ?? '').replace('#', '');
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map(char => char + char)
          .join('')
      : clean;

  if (full.length !== 6) {
    return { r: 100, g: 100, b: 100 };
  }

  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16)
  };
}

/**
 * Export `badgeBackground` — a light tint of the badge color, matching the
 * system badges (colored pill with a low-opacity background).
 */

export function badgeBackground(hex: string): string {
  const { r, g, b } = toRgb(hex);

  return `rgba(${r}, ${g}, ${b}, 0.16)`;
}

/**
 * Export `badgeForeground` — a darker shade of the badge color, readable on the
 * light tint (same-hue text, like the system badges).
 */

export function badgeForeground(hex: string): string {
  const { r, g, b } = toRgb(hex);
  const factor = 0.55;

  return `rgb(${Math.round(r * factor)}, ${Math.round(g * factor)}, ${Math.round(b * factor)})`;
}
